/* ============================================================
   Kaizen Seven — EFW PDP hero: Split Panel gallery + Purchase Block
   - Gallery: cross-fade via thumbnails, swipe/drag (>=40px, wrapping)
     and arrow keys; hovering swaps the dots for the thumbnail bar;
     scrolling the info column reveals the thumbnails.
   - Purchase block: one-time / subscribe radio toggle (border-only
     selection), collapsing delivery-frequency row (150ms height),
     live price on the ATC + sticky bar, selling_plan wired into the
     cart/add.js payload. Preview mode (no selling plan) disables
     checkout for the Subscribe option.
   - Bottom fade only while the column has more below.
   - Hero settle-in on load; Add to Cart via cart/add.js wired to the
     theme's cart drawer (`cart:refresh`) with an "Added to cart" label
     for 1.8s; frosted sticky ATC slides in once the in-flow ATC scrolls
     out of view (both breakpoints).
   ============================================================ */
(function () {
  'use strict';

  var ADDED_REVERT_MS = 1800;
  var SWIPE_MIN_PX = 40;

  function init(root) {
    if (root.dataset.efwPdpInit) return;
    root.dataset.efwPdpInit = '1';

    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var mqMobile = window.matchMedia('(max-width: 899.98px)');
    var mqHover = window.matchMedia('(hover: hover)');

    /* ---- Hero settle-in (once, on mount) ---- */
    var img = root.querySelector('[data-efw-pdp-img]');
    if (img && !reduce) {
      root.classList.add('efw-pdp--js');
      setTimeout(function () { img.classList.add('settle'); }, 150);
    }

    /* ---- Gallery ---- */
    var gallery = root.querySelector('[data-efw-gallery]');
    var slides = gallery ? Array.prototype.slice.call(gallery.querySelectorAll('[data-efw-slide]')) : [];
    var thumbs = gallery ? Array.prototype.slice.call(gallery.querySelectorAll('[data-efw-thumb]')) : [];
    var dots = gallery ? Array.prototype.slice.call(gallery.querySelectorAll('.efw-pdp__dot')) : [];
    var counter = gallery ? gallery.querySelector('[data-efw-counter]') : null;
    var count = slides.length;
    var active = 0;

    function goTo(i) {
      if (count < 2) return;
      active = ((i % count) + count) % count;
      slides.forEach(function (el, j) {
        var on = j === active;
        el.classList.toggle('is-active', on);
        el.setAttribute('aria-hidden', on ? 'false' : 'true');
      });
      thumbs.forEach(function (el, j) {
        var on = j === active;
        el.classList.toggle('is-active', on);
        el.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      dots.forEach(function (el, j) { el.classList.toggle('is-active', j === active); });
      if (counter) counter.textContent = (active + 1) + ' / ' + count;
    }

    function showThumbs() { if (gallery && count > 1) gallery.classList.add('thumbs-on'); }
    function hideThumbs() { if (gallery) gallery.classList.remove('thumbs-on'); }

    if (gallery && count > 1) {
      thumbs.forEach(function (btn, i) {
        btn.addEventListener('click', function () { goTo(i); });
      });
      gallery.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        if (!thumbs.length || thumbs.indexOf(document.activeElement) === -1) return;
        e.preventDefault();
        goTo(active + (e.key === 'ArrowRight' ? 1 : -1));
        if (thumbs[active]) thumbs[active].focus();
      });
      gallery.addEventListener('mouseenter', function () { if (mqHover.matches) showThumbs(); });
      gallery.addEventListener('mouseleave', function () { if (mqHover.matches) hideThumbs(); });

      var sx = null, sy = null;
      gallery.addEventListener('pointerdown', function (e) {
        if (e.button !== undefined && e.button !== 0) return;
        sx = e.clientX; sy = e.clientY;
      });
      gallery.addEventListener('pointerup', function (e) {
        if (sx === null) return;
        var dx = e.clientX - sx, dy = e.clientY - sy;
        sx = sy = null;
        if (Math.abs(dx) < SWIPE_MIN_PX || Math.abs(dx) < Math.abs(dy)) return;
        goTo(active + (dx < 0 ? 1 : -1));
      });
      gallery.addEventListener('pointercancel', function () { sx = sy = null; });

      document.addEventListener('shopify:block:select', function (e) {
        var idx = slides.indexOf(e.target);
        if (idx > -1) goTo(idx);
      });
    }

    /* ---- Info column: scroll-to-reveal thumbs + bottom fade ---- */
    var box = root.querySelector('[data-efw-scroll]');
    var details = root.querySelector('[data-efw-ingredients]');

    function updateMore() {
      if (!box) return;
      var more = !mqMobile.matches && (box.scrollHeight - box.scrollTop - box.clientHeight) > 4;
      root.classList.toggle('efw-pdp--more', more);
    }

    if (box) {
      var lastTop = box.scrollTop;
      box.addEventListener('scroll', function () {
        if (box.scrollTop > lastTop + 1 && !mqMobile.matches && mqHover.matches) showThumbs();
        lastTop = box.scrollTop;
        updateMore();
      }, { passive: true });
      if (details) details.addEventListener('toggle', updateMore);
      window.addEventListener('resize', updateMore);
      if (window.ResizeObserver) {
        var ro = new ResizeObserver(updateMore);
        ro.observe(box);
        Array.prototype.slice.call(box.children).forEach(function (el) { ro.observe(el); });
      }
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(updateMore);
      updateMore();
      setTimeout(updateMore, 400);
    }

    /* ---- Purchase block: toggle + frequency + live price ---- */
    var form = root.querySelector('[data-efw-pdp-form]');
    var atc = root.querySelector('[data-efw-pdp-atc]');
    var toggle = root.querySelector('[data-efw-toggle]');
    var opts = toggle ? Array.prototype.slice.call(toggle.querySelectorAll('[data-efw-opt]')) : [];
    var freq = root.querySelector('[data-efw-freq]');
    var planSelect = root.querySelector('[data-efw-plan-select]');
    var planInput = root.querySelector('[data-efw-plan]');
    var priceEls = Array.prototype.slice.call(root.querySelectorAll('[data-efw-pdp-price]'));
    var labels = Array.prototype.slice.call(root.querySelectorAll('[data-efw-pdp-label]'));
    var defaultLabels = labels.map(function (el) { return el.textContent; });
    var isPreview = !!(toggle && toggle.hasAttribute('data-efw-preview'));
    var previewLabel = atc ? (atc.getAttribute('data-preview-label') || 'Subscription coming soon') : '';
    var selected = 'once';
    var atcAvailable = !!(atc && !atc.disabled);

    function setPrice(text) {
      priceEls.forEach(function (el) { el.textContent = text; });
    }

    function currentPlanPrice() {
      if (planSelect && planSelect.selectedOptions && planSelect.selectedOptions[0]) {
        var p = planSelect.selectedOptions[0].getAttribute('data-price');
        if (p) return p;
      }
      var subOpt = opts.filter(function (o) { return o.getAttribute('data-efw-opt') === 'sub'; })[0];
      return subOpt ? subOpt.getAttribute('data-price') : '';
    }

    function syncPlanInput() {
      if (!planInput) return;
      if (selected === 'sub' && planSelect && !isPreview) {
        planInput.value = planSelect.value;
        planInput.disabled = false;
      } else {
        planInput.value = '';
        planInput.disabled = true;
      }
    }

    function setFreq(open) {
      if (!freq) return;
      var inner = freq.firstElementChild;
      var h = inner ? inner.offsetHeight : 0;
      if (open) {
        freq.style.height = reduce ? 'auto' : h + 'px';
      } else {
        /* an open row rests at height:auto (font-swap / resize safe); pin it
           first so the collapse transition has a start value */
        if (freq.style.height === 'auto') { freq.style.height = h + 'px'; void freq.offsetHeight; }
        freq.style.height = '0px';
      }
      freq.setAttribute('aria-hidden', open ? 'false' : 'true');
      if (planSelect) planSelect.tabIndex = open ? 0 : -1;
    }
    if (freq) {
      freq.addEventListener('transitionend', function (e) {
        if (e.target === freq && e.propertyName === 'height' && selected === 'sub') freq.style.height = 'auto';
      });
    }
    var evalSticky = null;

    function select(which) {
      selected = which;
      opts.forEach(function (o) {
        var on = o.getAttribute('data-efw-opt') === which;
        o.classList.toggle('is-selected', on);
        o.setAttribute('aria-checked', on ? 'true' : 'false');
        o.tabIndex = on ? 0 : -1; /* APG radio group: one tab stop, arrows move */
      });
      var onceOpt = opts.filter(function (o) { return o.getAttribute('data-efw-opt') === 'once'; })[0];
      setPrice(which === 'sub' ? currentPlanPrice() : (onceOpt ? onceOpt.getAttribute('data-price') : ''));
      setFreq(which === 'sub');
      syncPlanInput();
      /* Preview mode: never let a shopper add a subscription that has no plan */
      if (isPreview && atc && atcAvailable) {
        var previewBlock = which === 'sub';
        atc.disabled = previewBlock;
        labels.forEach(function (el, i) { el.textContent = previewBlock ? previewLabel : defaultLabels[i]; });
      }
      if (evalSticky) evalSticky();
    }

    if (opts.length) {
      opts.forEach(function (o) {
        o.addEventListener('click', function () { select(o.getAttribute('data-efw-opt')); });
      });
      toggle.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
        e.preventDefault();
        var next = selected === 'once' ? 'sub' : 'once';
        select(next);
        var target = opts.filter(function (o) { return o.getAttribute('data-efw-opt') === next; })[0];
        if (target) target.focus();
      });
      if (planSelect) {
        planSelect.addEventListener('change', function () {
          var subOpt = opts.filter(function (o) { return o.getAttribute('data-efw-opt') === 'sub'; })[0];
          var subPriceEl = subOpt ? subOpt.querySelector('.efw-pdp__opt-price') : null;
          if (subPriceEl) subPriceEl.textContent = currentPlanPrice();
          if (selected === 'sub') { setPrice(currentPlanPrice()); syncPlanInput(); }
        });
      }
      window.addEventListener('resize', function () { if (selected === 'sub') setFreq(true); });
      select('once');
    }

    /* ---- Add to cart ---- */
    var addedLabel = root.getAttribute('data-added-label') || 'Added to cart';
    var adding = false;
    var revertTimer = null;

    function setAdded() {
      labels.forEach(function (el) { el.textContent = addedLabel; });
      clearTimeout(revertTimer);
      revertTimer = setTimeout(function () {
        labels.forEach(function (el, i) { el.textContent = defaultLabels[i]; });
        adding = false;
      }, ADDED_REVERT_MS);
    }

    function addToCart() {
      if (adding || !form) return;
      if (atc && atc.disabled) return;
      var variantInput = form.querySelector('[name="id"]');
      if (!variantInput || !variantInput.value) return;
      adding = true;

      var item = { id: Number(variantInput.value), quantity: 1 };
      if (planInput && !planInput.disabled && planInput.value) item.selling_plan = Number(planInput.value);

      var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(rootUrl + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [item], sections: 'cart-drawer' })
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Add to cart failed (' + response.status + ')');
          return response.json();
        })
        .then(function (response) {
          if (response.sections && response.sections['cart-drawer']) {
            window.dispatchEvent(new window.CustomEvent('cart:refresh', {
              detail: { payload: response.sections['cart-drawer'] }
            }));
          }
          setAdded();
        })
        .catch(function (err) {
          console.error('EFW PDP:', err);
          adding = false;
          form.submit();
        });
    }

    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        addToCart();
      });
    }

    /* ---- Frosted sticky ATC: shows once the in-flow ATC is out of view
       (scrolled past inside the column, or the panel scrolled off-page) ---- */
    var sticky = root.querySelector('[data-efw-pdp-sticky]');
    if (sticky && atc) {
      sticky.addEventListener('click', addToCart);

      function setShown(show) {
        if (!show && document.activeElement === sticky) sticky.blur();
        sticky.classList.toggle('show', show);
        sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
        sticky.tabIndex = show ? 0 : -1;
      }

      var lastEntry = null;
      evalSticky = function () {
        if (!lastEntry) return;
        var vh = window.innerHeight || document.documentElement.clientHeight;
        var above = atc.getBoundingClientRect().top < vh * 0.65;
        setShown(!lastEntry.isIntersecting && above && !(isPreview && selected === 'sub'));
      };
      var io = new IntersectionObserver(function (entries) {
        lastEntry = entries[entries.length - 1];
        evalSticky();
      }, { threshold: 0 });
      io.observe(atc);

      function onMq() {
        setShown(false);
        io.unobserve(atc);
        io.observe(atc);
        updateMore();
      }
      if (mqMobile.addEventListener) mqMobile.addEventListener('change', onMq);
      else if (mqMobile.addListener) mqMobile.addListener(onMq);
    }
  }

  function initAll() {
    document.querySelectorAll('[data-efw-pdp]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-efw-pdp]');
    if (root) init(root);
  });
})();
